import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { brandsAPI, contentAPI } from '../utils/api';
import { FileText, Image, Video, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const ContentPage = () => {
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBrands();
  }, []);

  useEffect(() => {
    if (selectedBrand) {
      fetchContent();
    }
  }, [selectedBrand]);

  const fetchBrands = async () => {
    try {
      const response = await brandsAPI.getAll();
      setBrands(response.data.brands);
      if (response.data.brands.length > 0) {
        setSelectedBrand(response.data.brands[0].id);
      }
    } catch (error) {
      toast.error('Failed to fetch brands');
    }
  };

  const fetchContent = async () => {
    setLoading(true);
    try {
      const response = await contentAPI.getAll({ brand_id: selectedBrand });
      setContent(response.data.content);
    } catch (error) {
      toast.error('Failed to fetch content');
    } finally {
      setLoading(false);
    }
  };

  const getContentIcon = (type) => {
    switch (type) {
      case 'blog':
        return <FileText className="w-5 h-5 text-blue-600" />;
      case 'image':
        return <Image className="w-5 h-5 text-purple-600" />;
      case 'video':
        return <Video className="w-5 h-5 text-red-600" />;
      default:
        return <FileText className="w-5 h-5 text-gray-600" />;
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold heading">Content Library</h1>
            <p className="text-gray-600 mt-2">View and manage your AI-generated content</p>
          </div>
          <div className="w-64">
            <Select value={selectedBrand} onValueChange={setSelectedBrand}>
              <SelectTrigger>
                <SelectValue placeholder="Select brand" />
              </SelectTrigger>
              <SelectContent>
                {brands.map((brand) => (
                  <SelectItem key={brand.id} value={brand.id}>
                    {brand.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-96">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : content.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {content.map((item) => (
              <Card key={item.id} className="card">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    {getContentIcon(item.content_type)}
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      item.status === 'published' ? 'bg-green-100 text-green-700' :
                      item.status === 'scheduled' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {item.status}
                    </span>
                  </div>
                  <CardTitle className="mt-2 text-lg">{item.title || item.content_type}</CardTitle>
                </CardHeader>
                <CardContent>
                  {item.body && (
                    <p className="text-sm text-gray-600 line-clamp-3 mb-3">{item.body}</p>
                  )}
                  <div className="flex items-center text-xs text-gray-500">
                    <Calendar className="w-3 h-3 mr-1" />
                    {new Date(item.created_at).toLocaleDateString()}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="card text-center py-16">
            <CardContent>
              <p className="text-gray-500">No content available yet. Automation will start generating content soon!</p>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ContentPage;
